import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import React, { useState } from 'react';
import styled from 'styled-components';
import { Checkbox, Container, Section, Text10, Text12, Text14, Text16, Wrapper } from '../../atoms';
import { Intro, Loading } from '../../molecules';

export default function DemonstrationRequestForm({
  title,
  nameLabel,
  entityLabel,
  emailLabel,
  phoneLabel,
  messageLabel,
  infoText,
  acceptText,
  sendLabel,
}) {
  const [inputs, setInputs] = useState({});
  const [accepted, setAccepted] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  const handleInput = (event) => {
    event.persist();
    setInputs((prevInputs) => ({ ...prevInputs, [event.target.name]: event.target.value }));
  };

  const [sendDemoRequest, { loading }] = useMutation(
    gql`
      mutation ($name: String!, $entity: String!, $email: String!, $phone: String, $message: String!, $marketingConsent: Boolean) {
        customDemoRequestSendEmail(name: $name, entity: $entity, email: $email, phone: $phone, message: $message, marketingConsent: $marketingConsent)
      }
    `,
    {
      onError: () => {
        setSuccess(false);
        setError(true);
      },
      onCompleted: (data) => {
        if (data.customDemoRequestSendEmail === true) {
          setInputs({});
          setAccepted(false);
          setSuccess(true);
          setError(false);
        } else {
          setSuccess(false);
          setError(true);
        }
      }
    }
  );

  const submitForm = (e) => {
    e.preventDefault();

    // The checkbox is an optional marketing opt-in, not a condition of asking
    // for a demo. Answering the request runs on legitimate interest (document
    // 5, purposes a and b); only the marketing use needs consent. Refusing to
    // send the form without it would be bundling the two together.
    setSuccess(false);
    setError(false);

    sendDemoRequest({
      variables: {
        name: inputs.name,
        entity: inputs.entity,
        email: inputs.email,
        phone: inputs.phone || '',
        message: inputs.message,
        marketingConsent: accepted
      }
    });
  };

  return (
    <Section backgroundColor="#fff">
      <Wrapper both noHeight>
        <Container lg>
          <Content>
            {title &&
              <Header>
                <Intro
                  centerContainer
                  titleSize="H3"
                  title={title}
                  titleColor="#35383B"
                  titleTextAlign="center"
                  titleMaxWidth="640px"
                />
              </Header>
            }
            <Form onSubmit={submitForm}>
              <Field>
                <Label color="#5D6368" font='InterBold' dangerouslySetInnerHTML={{ __html: nameLabel }} />
                <Margin />
                <Input type='text' name='name' value={inputs.name || ''} onChange={handleInput} required />
              </Field>
              <Field>
                <Label color="#5D6368" font='InterBold' dangerouslySetInnerHTML={{ __html: entityLabel }} />
                <Margin />
                <Input type='text' name='entity' value={inputs.entity || ''} onChange={handleInput} required />
              </Field>
              <Field>
                <Label color="#5D6368" font='InterBold' dangerouslySetInnerHTML={{ __html: emailLabel }} />
                <Margin />
                <Input type='email' name='email' value={inputs.email || ''} onChange={handleInput} required />
              </Field>
              <Field>
                <Label color="#5D6368" font='InterBold' dangerouslySetInnerHTML={{ __html: phoneLabel }} />
                <Margin />
                <Input type='text' name='phone' value={inputs.phone || ''} onChange={handleInput} />
              </Field>
              <Field>
                <Label color="#5D6368" font='InterBold' dangerouslySetInnerHTML={{ __html: messageLabel }} />
                <Margin />
                <Textarea name='message' value={inputs.message || ''} onChange={handleInput} required />
              </Field>

              {infoText &&
                <>
                  <Margin margin={2} />
                  <InfoText color="#B2B6B9" dangerouslySetInnerHTML={{ __html: infoText }} />
                </>
              }

              {acceptText &&
                <>
                  <Margin margin={3} />
                  <AcceptRow>
                    <AcceptLabel>
                      <CheckboxBig>
                        <Checkbox
                          checked={accepted}
                          onClick={() => setAccepted(!accepted)}
                        />
                      </CheckboxBig>
                    </AcceptLabel>
                    <AcceptText color="#35383B" dangerouslySetInnerHTML={{ __html: acceptText }} />
                  </AcceptRow>
                </>
              }

              <Margin margin={4.5} />

              {!loading &&
                <ButtonSubmit color='368D67' type='submit'>
                  <svg>
                    <rect x="0" y="0" fill="none" width="100%" height="100%" />
                  </svg>
                  <Text14 color="#368D67" dangerouslySetInnerHTML={{ __html: sendLabel }} />
                </ButtonSubmit>
              }
              {loading &&
                <LoadingWrap>
                  <Loading small isLoading={loading} />
                </LoadingWrap>
              }

              {success &&
                <>
                  <Margin margin={2} />
                  <StatusText color="#368D67">Pedido enviado com sucesso.</StatusText>
                </>
              }
              {error &&
                <>
                  <Margin margin={2} />
                  <StatusText color="#D14343">Ocorreu um erro ao enviar o pedido.</StatusText>
                </>
              }
            </Form>
          </Content>
        </Container>
      </Wrapper>
    </Section>
  );
}

const Margin = styled.div`
  margin-top: ${({ margin }) => margin ? margin * 8 : 8}px;
`;

const Content = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
`;

const Header = styled.div`
  width: 100%;
`;

const Form = styled.form`
  width: 440px;
  max-width: 100%;
  margin-top: 36px;
`;

const Field = styled.div`
  margin-top: 16px;
  :first-child{
    margin-top: 0;
  }
`;

const Label = styled(Text10)`
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const Input = styled.input`
  width: 440px;
  max-width: 100%;
  height: 52px;
  background: #EBECED;
  border: 1px solid #EBECED;
  border-radius: 2px;
  padding: 12px 16px;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 17px;
  color: #5D6368;
  font-family: 'InterRegular', sans-serif;
  transition: all 0.4s ease-in-out;
  :hover{
    cursor: pointer;
    border: 1px solid #B2B6B9;
  }
  :focus{
    border: 1px solid #B2B6B9;
  }
`;

const Textarea = styled.textarea`
  width: 440px;
  max-width: 100%;
  height: 104px;
  background: #EBECED;
  border: 1px solid #EBECED;
  border-radius: 2px;
  padding: 12px 16px;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 17px;
  color: #5D6368;
  font-family: 'InterRegular', sans-serif;
  transition: all 0.4s ease-in-out;
  resize: vertical;
  :hover{
    cursor: pointer;
    border: 1px solid #B2B6B9;
  }
  :focus{
    border: 1px solid #B2B6B9;
  }
`;

const InfoText = styled(Text12)`
  font-size: 16px;
  line-height: 22px;
  p{
    margin-block-start: 0;
    margin-block-end: 0;
    margin-inline-start: 0;
    margin-inline-end: 0;
  }
`;

const AcceptRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const AcceptLabel = styled.label`
  display: flex;
  align-items: center;
`;

const AcceptText = styled(Text10)`
  color: #35383B;
  font-size: 16px;
  line-height: 22px;
  a{
    text-decoration: underline;
    color: #35383B;
  }
  a:hover{
    color: #35383B;
  }
`;

const CheckboxBig = styled.div`
  display: flex;
  transform: scale(1.35);
  transform-origin: left center;
`;

const ButtonSubmit = styled.button`
  margin: 0 auto;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  height: 52px;
  width: 176px;
  box-sizing: border-box;
  transition: all 0.5s ease-in-out;
  background: transparent;
  border: 0;
  svg {
    height: 52px;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }
  rect {
    fill: none;
    stroke: ${({ color }) => color ? '#' + color : '#368D67'};
    stroke-width: 1;
    stroke-dasharray: 422, 0;
    transition: all 0.5s ease-in-out;
  }
  :hover{
    cursor: pointer;
    rect {
      stroke-width: 1;
      stroke-dasharray: 35, 310;
      stroke-dashoffset: 48;
      transition: all 1.35s cubic-bezier(0.19, 1, 0.22, 1);
    }
  }
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
`;

const StatusText = styled(Text16)`
  text-align: center;
`;
